<template>
<span id="add-blocked-account-dialog" v-if="showAddBlockedAccount()">
  <el-button id="btn-add-user" type="success" plain @click="dialogVisible = true" size="mini">
    {{ $t('blockedAccounts.addBlock') }}
  </el-button>
  <el-dialog
    :close-on-click-modal="false"
    v-on:open="handleOpen"
    :title="$t('blockedAccounts.title')"
    :visible.sync="dialogVisible"
    width="65%"
    class="dialog-with-cols"
  >
  <el-form :model="form" :rules="formRules" ref="form" label-position="top" size="mini">
    <el-row>
      <el-col :md="8" :sm="12" :xs="12" class="form-vertical-item">
        <el-form-item :label="$t('blockedAccounts.warehouse')" prop="warehouseId">
           <outbound-warehouse-select
             v-model="form.warehouseId"
             :includeOptionAll="false"
             :disabled="form.shipperAccountId"
           ></outbound-warehouse-select>
        </el-form-item>
      </el-col>
      <el-col :md="8" :sm="12" :xs="12" class="form-vertical-item">
        <el-form-item :label="$t('blockedAccounts.shippers')" prop="shipperAccountId">
          <autocomplete
            :model.sync="form.shipperAccountId"
            :url="urlShipperAutocomplete"
            labelField="name"
            labelFieldLastWithDash="number"
            :classCss="null"
            valueField="id"
            id="searchForm-shipper"
            ref="search-shipper-list"
            @change="clearWarehouse"
          ></autocomplete>
        </el-form-item>
      </el-col>
      <el-col :md="8" :sm="12" :xs="12" class="form-vertical-item">
        <el-form-item :label="$t('blockedAccounts.consignee')" prop="consigneeAccountId">
          <autocomplete
            :model.sync="form.consigneeAccountId"
            :shipperAccountId="null"
            labelFieldLastWithDash="number"
            :url="urlConsigneeAutocomplete"
            labelField="name"
            valueField="id"
            ref="filter-consignee"
            id="searchForm-consignee"
            @change="clearWarehouse"
          ></autocomplete>
        </el-form-item>
      </el-col>
      <el-col :md="8" :sm="12" :xs="12" class="form-vertical-item">
        <el-form-item :label="$t('blockedAccounts.dateFrom')" prop="startDate">
          <el-date-picker
            class="el-dropdown-new drop-sp date-dob"
            v-model="form.startDate"
            format="MM/dd/yyyy"
            clearable
            type="date"
            id="searchForm-blockedDateFrom"
            ref="filter-blockedDateFrom"
            :picker-options="blockedAccountDatePickerOptions"
          ></el-date-picker>
        </el-form-item>
      </el-col>
      <el-col :md="8" :sm="12" :xs="12" class="form-vertical-item">
        <el-form-item :label="$t('blockedAccounts.dateTo')" prop="endDate">
          <el-date-picker
            class="el-dropdown-new drop-sp date-dob"
            v-model="form.endDate"
            format="MM/dd/yyyy"
            clearable
            type="date"
            id="searchForm-blockedDateTo"
            ref="filter-blockedDateTo"
            :picker-options="blockedAccountDatePickerOptions"
          ></el-date-picker>
        </el-form-item>
      </el-col>
      <el-col :md="8" :sm="12" :xs="12" class="form-vertical-item">
        <el-form-item :label="$t('blockedAccounts.notes')" prop="notes">
          <el-input
            v-model="form.notes"
            clearable
            maxlength="500"
            type="textarea"
            :rows="2"
            id="notes-input"
          ></el-input>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
  <div id="add-dialog-footer" slot="footer" class="dialog-footer form-vertical-item flex-reverse-direction">
    <el-button type="primary" id="form-save-button" @click="submitForm()" size="mini">{{ $t('blockedAccounts.addBlock') }}</el-button>
    <el-button id="form-refresh-button" @click="resetForm()" size="mini">{{ $t('blockedAccounts.clear') }}</el-button>
  </div>
</el-dialog>
</span>
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';
import { Message } from 'element-ui';
import apiConstants from '@/utils/apiConstants';
import Autocomplete from '@/components/Autocomplete';
import OutboundWarehouseSelect from '@/views/routing/components/OutboundWarehouseSelect';
import constants from '@/utils/constants';
import { BLOCKED_ACCOUNTS_SAVE, BLOCKED_ACCOUNTS_SEARCH } from '@/store/actions';

export default {
  name: 'BlockedAccountAddDialog',
  components: {
    Autocomplete,
    OutboundWarehouseSelect
  },
  computed: {
    urlShipperAutocomplete: () => apiConstants.END_POINTS.ACCOUNTS.SHIPPERS,
    urlConsigneeAutocomplete: () => apiConstants.END_POINTS.ACCOUNTS.CONSIGNEE,
    blockedAccountDatePickerOptions: () => ({
      disabledDate(time) {
        return moment(time.getTime()).isBefore(Date.now(), 'day');
      }
    }),
  },
  data() {
    const validateShipperConsigneeOrWarehouse = (rule, value, callback) => {
      if (!this.form.warehouseId && !this.form.shipperAccountId && !this.form.consigneeAccountId) {
        callback(new Error(this.$t('blockedAccounts.errors.shipperConsigneeOrWarehouseRequired')));
      }

      callback();
    };

    const validateConsigneeAndWarehouse = (rule, value, callback) => {
      if (this.form.consigneeAccountId && !this.form.shipperAccountId && !this.form.warehouseId) {
        callback(new Error(this.$t('blockedAccounts.errors.consigneeAndWarehouseRequired')));
      }

      callback();
    };

    const validateDateRange = (rule, value, callback) => {
      if (moment(this.form.startDate).isAfter(moment(this.form.endDate))
          || moment(this.form.endDate).isBefore(moment(this.form.startDate))) {
        callback(new Error(this.$t('blockedAccounts.errors.dateRangeInvalid')));
      }

      callback();
    };

    return {
      dialogVisible: false,
      form: {
        warehouseId: null,
        shipperAccountId: null,
        consigneeAccountId: null,
        startDate: null,
        endDate: null,
        notes: null
      },
      formRules: {
        shipperAccountId: [
          { validator: validateShipperConsigneeOrWarehouse, trigger: 'submit' }
        ],
        consigneeAccountId: [
          { validator: validateShipperConsigneeOrWarehouse, trigger: 'submit' }
        ],
        warehouseId: [
          { validator: validateShipperConsigneeOrWarehouse, trigger: 'submit' },
          { validator: validateConsigneeAndWarehouse, trigger: 'submit' }
        ],
        startDate: [
          { required: true, message: this.$t('blockedAccounts.errors.dateFromRequired'), trigger: 'blur' },
          { validator: validateDateRange, trigger: 'submit' }
        ],
        endDate: [
          { required: true, message: this.$t('blockedAccounts.errors.dateToRequired'), trigger: 'blur' },
          { validator: validateDateRange, trigger: 'submit' }
        ],
        notes: [
          { required: true, message: this.$t('blockedAccounts.errors.notesRequired'), trigger: 'blur' }
        ],
      }
    };
  },
  methods: {
    ...mapActions({
      search: BLOCKED_ACCOUNTS_SEARCH,
      save: BLOCKED_ACCOUNTS_SAVE
    }),
    handleOpen() {
      this.$nextTick(() => {
        this.resetForm();
      });
    },
    resetForm() {
      this.$refs.form.resetFields();
      this.form.warehouseId = null;
      this.form.shipperAccountId = null;
      this.form.consigneeAccountId = null;
      this.form.startDate = null;
      this.form.endDate = null;
      this.form.notes = null;
    },
    showAddBlockedAccount() {
      return this.$can(this.permissions.WAREHOUSE.ADD_BLOCKED_ACCOUNT);
    },
    submitForm() {
      this.$refs.form.validate()
        .then((validForm) => {
          if (!validForm) return;

          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          this.clearWarehouse();
          this.save(this.form)
            .then((response) => {
              this.$message.success(response.message);
              this.search({});
            })
            .finally(() => {
              loading.close();
            });
        })
        .catch(() => {});
    },
    clearWarehouse() {
      if (this.form.shipperAccountId) {
        this.form.warehouseId = null;
      }
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss">
    #add-blocked-account-dialog  {
        .el-select, .el-date-editor {
            width: 100% !important;
        }
    }
</style>
